import type { AppPermission } from '@threadline/contracts';
import { lazy, Suspense, type ReactNode } from 'react';
import { useAccess } from '@/hooks/use-access';
import { Skeleton } from '@/components/ui/skeleton';

const NotFoundPage = lazy(() => import('@/pages/not-found'));

/**
 * Wraps a route in the permission that opens it.
 *
 * Hiding the menu is not the control - a typed URL has to meet the same rule,
 * and it answers "not found" rather than "forbidden": the menu is already
 * hidden, and a refusal would undo that by confirming the page is there.
 *
 * Nothing renders until the answer arrives, so a page never flashes into view
 * and then disappears.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: AppPermission;
  children: ReactNode;
}) {
  const { can, isReady } = useAccess();

  if (!isReady) {
    return <Skeleton className="m-4 h-40" />;
  }

  return can(permission) ? (
    <>{children}</>
  ) : (
    <Suspense fallback={null}>
      <NotFoundPage />
    </Suspense>
  );
}
